import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import GameCard from '@/components/base/GameCard';
import { categories } from '@/mocks/categories';
import { getGamesByCategory, getHotGames } from '@/mocks/games';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [activeTab, setActiveTab] = useState<string>('all');

  const games = slug ? getGamesByCategory(slug) : [];
  const hotGames = getHotGames();

  const filteredGames = activeTab === 'hot'
    ? games.filter((g) => g.isHot)
    : activeTab === 'new'
    ? games.filter((g) => g.isNew)
    : games;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    setActiveTab('all');
  }, [slug]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-800">
              <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
            </div>
            <h2 className="mt-4 text-xl font-heading font-bold text-foreground-200">分类未找到</h2>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-400 hover:text-primary-300 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory.seoTitle,
    description: currentCategory.seoDesc,
    url: `https://www.jinshaentertainment.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredGames.map((game, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: game.name,
      })),
    },
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: currentCategory.seoTitle,
    description: currentCategory.seoDesc,
    url: `https://www.jinshaentertainment.com/category/${slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageStructuredData) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-900/60 border-b border-background-700/30">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
          <div className="w-full px-4 md:px-6 pb-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10">
                  <i className={`${currentCategory.icon} text-primary-400 text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl ml-[52px]">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'all'
                    ? 'bg-primary-500 text-background-950'
                    : 'text-foreground-400 hover:text-foreground-200 bg-background-900 border border-background-700/30'
                }`}
              >
                全部
              </button>
              <button
                onClick={() => setActiveTab('hot')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'hot'
                    ? 'bg-accent-500 text-foreground-50'
                    : 'text-foreground-400 hover:text-foreground-200 bg-background-900 border border-background-700/30'
                }`}
              >
                热门推荐
              </button>
              <button
                onClick={() => setActiveTab('new')}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'new'
                    ? 'bg-secondary-500 text-foreground-50'
                    : 'text-foreground-400 hover:text-foreground-200 bg-background-900 border border-background-700/30'
                }`}
              >
                新品上架
              </button>
            </div>

            {filteredGames.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-product-shop="">
                {filteredGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-800">
                  <i className="ri-inbox-line text-foreground-500 text-2xl"></i>
                </div>
                <p className="mt-4 text-sm text-foreground-500">该分类下暂无内容</p>
              </div>
            )}
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-12 bg-background-900/40">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-6">
              热门推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-product-shop="">
              {hotGames.slice(0, 4).map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">浏览更多分类：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'text-foreground-500 bg-background-900 border border-background-700/30 hover:text-foreground-300 hover:border-background-500/50'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}